import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import SuperAdminLayout from './SuperAdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://dare.exlva.com';

const AWSS3Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({
    access_key_id: '',
    secret_access_key: '',
    bucket_name: '',
    base_url: '',
    region: 'us-east-1',
    is_enabled: false,
    secret_access_key_set: false,
    last_tested_at: null,
    last_test_success: null,
    last_error: null
  });

  // AWS regions list
  const AWS_REGIONS = [
    { value: 'us-east-1', label: 'US East (N. Virginia)' },
    { value: 'us-east-2', label: 'US East (Ohio)' },
    { value: 'us-west-1', label: 'US West (N. California)' },
    { value: 'us-west-2', label: 'US West (Oregon)' },
    { value: 'eu-west-1', label: 'EU (Ireland)' },
    { value: 'eu-west-2', label: 'EU (London)' },
    { value: 'eu-central-1', label: 'EU (Frankfurt)' },
    { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
    { value: 'sa-east-1', label: 'South America (São Paulo)' }
  ];

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('exlva_dare_user') || '{}');
    if (!user.id || !['super_admin', 'admin'].includes(user.role)) {
      navigate('/login');
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    const user = JSON.parse(localStorage.getItem('exlva_dare_user') || '{}');
    try {
      const response = await axios.get(`${API_URL}/api/admin/s3-settings?user_id=${user.id}`);
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data,
          secret_access_key: '' // Never populate the secret key field
        }));
      }
    } catch (error) {
      console.log('Using default S3 settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const user = JSON.parse(localStorage.getItem('exlva_dare_user') || '{}');
    if (!user.id) {
      setError('Please log in again');
      return;
    }

    // Validation
    if (!settings.access_key_id.trim()) {
      setError('AWS Access Key ID is required');
      return;
    }
    if (!settings.secret_access_key_set && !settings.secret_access_key.trim()) {
      setError('AWS Secret Access Key is required');
      return;
    }
    if (!settings.bucket_name.trim()) {
      setError('S3 Bucket Name is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        access_key_id: settings.access_key_id,
        bucket_name: settings.bucket_name,
        base_url: settings.base_url,
        region: settings.region,
        is_enabled: settings.is_enabled
      };

      // Only include secret key if it's being updated
      if (settings.secret_access_key.trim()) {
        updateData.secret_access_key = settings.secret_access_key;
      }

      const response = await axios.put(
        `${API_URL}/api/admin/s3-settings?user_id=${user.id}`,
        updateData
      );
      
      if (response.data.success) {
        setSuccess('S3 settings saved successfully!');
        if (response.data.settings) {
          setSettings(prev => ({
            ...prev,
            ...response.data.settings,
            secret_access_key: '' // Clear secret key field after save
          }));
        }
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to save S3 settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    const user = JSON.parse(localStorage.getItem('exlva_dare_user') || '{}');
    if (!user.id) {
      setError('Please log in again');
      return;
    }

    setTesting(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/s3-settings/test?user_id=${user.id}`
      );
      
      if (response.data.success) {
        setSuccess(response.data.message);
        // Refresh settings to get updated test status
        await fetchSettings();
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'S3 connection test failed');
      // Refresh settings to get updated test status
      await fetchSettings();
    } finally {
      setTesting(false);
    }
  };

  const handleClear = async () => {
    const user = JSON.parse(localStorage.getItem('exlva_dare_user') || '{}');
    if (!user.id) {
      setError('Please log in again');
      return;
    }

    if (!window.confirm('Are you sure you want to clear all S3 settings? This will disable file storage.')) {
      return;
    }

    setClearing(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.delete(
        `${API_URL}/api/admin/s3-settings?user_id=${user.id}`
      );
      
      if (response.data.success) {
        setSuccess('S3 settings cleared successfully');
        setSettings({
          access_key_id: '',
          secret_access_key: '',
          bucket_name: '',
          base_url: '',
          region: 'us-east-1',
          is_enabled: false,
          secret_access_key_set: false,
          last_tested_at: null,
          last_test_success: null,
          last_error: null
        });
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to clear S3 settings');
    } finally {
      setClearing(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccess('');
  };

  // Configuration status
  const isConfigured = settings.access_key_id && settings.secret_access_key_set && settings.bucket_name;

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">AWS S3 Configuration</h1>
            <p className="mt-1 text-sm text-gray-600">
              Configure Amazon S3 for secure file storage and retrieval.
            </p>
          </div>
          <div className="flex items-center gap-4">
            {/* Status Badge */}
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isConfigured && settings.is_enabled
                ? 'bg-green-100 text-green-800'
                : isConfigured
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-gray-100 text-gray-800'
            }`}>
              {isConfigured && settings.is_enabled
                ? '✓ Configured & Enabled'
                : isConfigured
                  ? '⚠ Configured (Disabled)'
                  : '○ Not Configured'}
            </div>
            <Button 
              variant="outline" 
              onClick={handleClear}
              disabled={clearing || saving || testing || !isConfigured}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              {clearing ? 'Clearing...' : 'Clear Configuration'}
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-green-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-green-700">{success}</p>
          </div>
        )}

        {/* AWS Credentials Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              AWS Credentials
            </CardTitle>
            <CardDescription>
              Enter your AWS IAM credentials with S3 access permissions.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Access Key ID */}
              <div className="space-y-2">
                <Label htmlFor="access_key_id">AWS Access Key ID *</Label>
                <Input
                  id="access_key_id"
                  value={settings.access_key_id}
                  onChange={(e) => handleInputChange('access_key_id', e.target.value)}
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  className="font-mono"
                />
              </div>

              {/* Secret Access Key */}
              <div className="space-y-2">
                <Label htmlFor="secret_access_key">
                  AWS Secret Access Key *
                  {settings.secret_access_key_set && (
                    <span className="ml-2 text-xs text-green-600 font-normal">
                      (Already configured - enter new value to replace)
                    </span>
                  )}
                </Label>
                <Input
                  id="secret_access_key"
                  type="password"
                  value={settings.secret_access_key}
                  onChange={(e) => handleInputChange('secret_access_key', e.target.value)}
                  placeholder={settings.secret_access_key_set ? '••••••••••••••••' : 'Enter secret access key'}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Security Note:</strong> Credentials are encrypted before storage. The secret key is never displayed after saving.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Bucket Configuration Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5 text-sky-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
              Bucket Configuration
            </CardTitle>
            <CardDescription>
              Configure your S3 bucket details and region.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Bucket Name */}
              <div className="space-y-2">
                <Label htmlFor="bucket_name">S3 Bucket Name *</Label>
                <Input
                  id="bucket_name"
                  value={settings.bucket_name}
                  onChange={(e) => handleInputChange('bucket_name', e.target.value)}
                  placeholder="my-dare-bucket"
                />
              </div>

              {/* Region */}
              <div className="space-y-2">
                <Label htmlFor="region">AWS Region *</Label>
                <select
                  id="region"
                  value={settings.region}
                  onChange={(e) => handleInputChange('region', e.target.value)}
                  className="w-full h-10 px-3 rounded-md border border-gray-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  {AWS_REGIONS.map(region => (
                    <option key={region.value} value={region.value}>
                      {region.label} ({region.value})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Base URL (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="base_url">
                S3 Base URL <span className="text-gray-400 font-normal">(Optional - for custom endpoints)</span>
              </Label>
              <Input
                id="base_url"
                value={settings.base_url}
                onChange={(e) => handleInputChange('base_url', e.target.value)}
                placeholder="https://s3.amazonaws.com (leave empty for default)"
              />
              <p className="text-sm text-gray-500">
                Only required for S3-compatible services or custom endpoints.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Status & Controls Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Status & Controls
            </CardTitle>
            <CardDescription>
              Enable or disable S3 storage and test your connection.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label htmlFor="is_enabled" className="text-base font-medium">Enable S3 Storage</Label>
                <p className="text-sm text-gray-500">
                  When enabled, all file uploads will use S3 with signed URLs.
                </p>
              </div>
              <Switch
                id="is_enabled"
                checked={settings.is_enabled}
                onCheckedChange={(checked) => handleInputChange('is_enabled', checked)}
                disabled={!isConfigured}
              />
            </div>

            {/* Test Connection Status */}
            {settings.last_tested_at && (
              <div className={`p-4 rounded-lg ${
                settings.last_test_success 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-red-50 border border-red-200'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {settings.last_test_success ? (
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  <span className={`font-medium ${settings.last_test_success ? 'text-green-800' : 'text-red-800'}`}>
                    {settings.last_test_success ? 'Connection Successful' : 'Connection Failed'}
                  </span>
                </div>
                <p className={`text-sm ${settings.last_test_success ? 'text-green-700' : 'text-red-700'}`}>
                  Last tested: {new Date(settings.last_tested_at).toLocaleString()}
                  {settings.last_error && (
                    <span className="block mt-1">Error: {settings.last_error}</span>
                  )}
                </p>
              </div>
            )}

            {/* Test Connection Button */}
            <div className="flex gap-4">
              <Button
                variant="outline"
                onClick={handleTestConnection}
                disabled={testing || saving || !isConfigured}
                className="flex items-center gap-2"
              >
                {testing ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Testing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Test Connection
                  </>
                )}
              </Button>
              {!isConfigured && (
                <p className="text-sm text-gray-500 flex items-center">
                  Save configuration first to test connection
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <Button 
            onClick={handleSave} 
            disabled={saving || testing || clearing}
            className="px-8"
          >
            {saving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save Configuration'
            )}
          </Button>
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default AWSS3Settings;
